// Cache Management Component for Admin
// Allows admins to clear cache and force updates

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  RefreshCw, 
  Trash2, 
  Download, 
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';
import cacheManager, { clearCache, forceUpdate, checkUpdates, getCacheInfo } from '../../utils/cacheManager';
import toast from 'react-hot-toast';

const CacheManagement = () => {
  const [cacheInfo, setCacheInfo] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Load cache info on component mount
  useEffect(() => {
    loadCacheInfo();
  }, []);

  const loadCacheInfo = async () => {
    try {
      const info = await getCacheInfo();
      setCacheInfo(info);
    } catch (error) {
      // Error loading cache info
    }
  };

  const handleClearCache = async () => {
    if (!window.confirm('Are you sure you want to clear all caches? This will force users to download fresh content.')) {
      return;
    }

    setIsLoading(true);
    try {
      await clearCache();
      await loadCacheInfo();
      toast.success('All caches cleared successfully!');
      setLastUpdate(new Date());
    } catch (error) {
      toast.error('Failed to clear cache: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForceUpdate = async () => {
    setIsLoading(true);
    try {
      await forceUpdate();
      toast.success('Force update initiated! Users will get the latest version.');
      setLastUpdate(new Date());
    } catch (error) {
      toast.error('Failed to force update: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckUpdates = async () => {
    setIsLoading(true);
    try {
      const hasUpdate = await checkUpdates();
      if (hasUpdate) {
        toast.success('Updates available! Users will get the latest version.');
      } else {
        toast('No updates available. App is up to date.', { icon: 'ℹ️' });
      }
      setLastUpdate(new Date());
    } catch (error) {
      toast.error('Failed to check for updates: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <RefreshCw className="h-6 w-6 mr-3 text-blue-500" />
              Cache Management
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Manage application cache and force updates for users
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Last Updated: {lastUpdate ? lastUpdate.toLocaleTimeString() : 'Never'}
            </div>
          </div>
        </div>
      </div>

      {/* Cache Information */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Info className="h-5 w-5 mr-2 text-blue-500" />
          Cache Information
        </h3>
        
        {Object.keys(cacheInfo).length > 0 ? (
          <div className="space-y-3">
            {Object.entries(cacheInfo).map(([cacheName, fileCount]) => (
              <div key={cacheName} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="font-medium text-gray-900 dark:text-white">{cacheName}</span>
                <span className="text-sm text-gray-600 dark:text-gray-400">{fileCount} files</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No cache information available</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Clear Cache */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <div className="text-center">
            <div className="bg-red-100 dark:bg-red-900/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="h-8 w-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Clear All Cache
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Remove all cached files and force fresh downloads
            </p>
            <button
              onClick={handleClearCache}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Clear Cache
            </button>
          </div>
        </motion.div>

        {/* Force Update */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <div className="text-center">
            <div className="bg-blue-100 dark:bg-blue-900/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Download className="h-8 w-8 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Force Update
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Force all users to get the latest version
            </p>
            <button
              onClick={handleForceUpdate}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Force Update
            </button>
          </div>
        </motion.div>

        {/* Check Updates */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <div className="text-center">
            <div className="bg-green-100 dark:bg-green-900/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Check Updates
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Check if updates are available for users
            </p>
            <button
              onClick={handleCheckUpdates}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Check Updates
            </button>
          </div>
        </motion.div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
              Cache Management Instructions
            </h4>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
              <li>• <strong>Clear Cache:</strong> Removes all cached files, forcing fresh downloads</li>
              <li>• <strong>Force Update:</strong> Notifies all users to reload and get the latest version</li>
              <li>• <strong>Check Updates:</strong> Verifies if new content is available</li>
              <li>• Use these tools when users report "stuck" or outdated content issues</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CacheManagement;
